import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Account, UtilizationSummary, AccountPosition } from '../types';
import { publicApiService } from '../services/publicApi';
import PositionCard from '../components/PositionCard';
import MarketThesis from '../components/MarketThesis';
import TierSpectrumExplainer from '../components/portfolio/TierSpectrumExplainer';
import { groupPositionsByTier } from '../tiers/resolveTierContext';
import TierChip from '../components/portfolio/TierChip';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  Wallet,
  TrendingUp,
  Target,
  ActivitySquare,
  Lock,
  ArrowRight,
} from 'lucide-react';
import PublicHeader from '../components/PublicHeader';

// Demo account ID is now stored on the server

const ReadOnlyDashboard: React.FC = () => {
  const [account, setAccount] = useState<Account | null>(null);
  const [summary, setSummary] = useState<UtilizationSummary | null>(null);
  const [activePositions, setActivePositions] = useState<AccountPosition[]>([]);
  const [positionActivityLogs, setPositionActivityLogs] = useState<
    Record<string, any[]>
  >({});
  const [positionAiReviews] = useState<Record<string, any>>({});
  const [positionPrices, setPositionPrices] = useState<Record<string, number>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDemoData = async () => {
      try {
        setLoading(true);
        const response = await publicApiService.getDemoAccount();
        setAccount(response.account);
        setSummary(response.utilizationSummary);
        setActivePositions(response.positions || []);

        // Use activity logs from the API response (same as logged-in dashboard)
        if (response.positionActivityLogs) {
          setPositionActivityLogs(response.positionActivityLogs);
        }

        // Extract prices from positions
        const prices: Record<string, number> = {};
        response.positions?.forEach((pos: AccountPosition) => {
          if (pos.actualPosition?.schwabInstrumentPrice) {
            prices[pos.symbol] = pos.actualPosition.schwabInstrumentPrice;
          } else if (pos.targetHolding?.expectedFillPrice) {
            prices[pos.symbol] = pos.targetHolding.expectedFillPrice;
          }
        });
        setPositionPrices(prices);
      } catch (err: any) {
        console.error('Failed to load demo account data:', err);
        setError(err.message || 'Failed to load demo data');
      } finally {
        setLoading(false);
      }
    };

    loadDemoData();
  }, []);

  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !account || !summary) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Unable to Load Demo Data
          </h2>
          <p className="text-gray-600 mb-4">{error || 'Account not found'}</p>
          <Link to="/register" className="text-primary-600 hover:underline">
            Join the Waitlist.
          </Link>
        </div>
      </div>
    );
  }

  const positionsByTier = groupPositionsByTier(activePositions, account);

  const totalUnrealizedPL = activePositions.reduce((sum, pos) => {
    const pl = pos.actualPosition?.schwabInstrumentUnrealizedProfitLoss || 0;
    return sum + pl;
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      {/* Demo Banner */}
      <div className="bg-primary-600 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            <span className="text-sm font-medium">
              Demo Mode - This is a read-only preview of of a live Marlin Trades
              dashboard.
            </span>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Portfolio command center</h1>
          <p className="mt-1 text-sm text-gray-500">
            Demo view — positions grouped by tier objective
          </p>
        </div>

        <TierSpectrumExplainer />

        {/* Account Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <Wallet className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Value</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {formatCurrency(account.balances.totalValue)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <TrendingUp className="h-8 w-8 text-success-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Unrealized P&L
                </p>
                <p
                  className={`text-2xl font-semibold ${
                    totalUnrealizedPL >= 0
                      ? 'text-success-600'
                      : 'text-danger-600'
                  }`}
                >
                  {formatCurrency(totalUnrealizedPL)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-warning-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Utilization</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {(account.utilizationPct * 100).toFixed(1)}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center">
              <ActivitySquare className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Active Positions
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {activePositions.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Current Positions by tier */}
        <div className="mb-8 space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Current positions by tier
          </h2>
          {([1, 2, 3, 4, 5, 6, 'unassigned'] as const).map((tierKey) => {
            const tierPositions = positionsByTier[tierKey];
            if (!tierPositions.length) return null;
            return (
              <div key={String(tierKey)} className="rounded-xl border border-gray-200 bg-white p-4">
                {tierKey === 'unassigned' ? (
                  <TierChip variant="unassigned" />
                ) : (
                  <TierChip riskTier={tierKey} />
                )}
                <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {tierPositions.map((position) => (
                    <PositionCard
                      key={position._id}
                      position={position}
                      account={account}
                      activityLogData={positionActivityLogs[position._id] || []}
                      aiReviewData={positionAiReviews[position._id] || null}
                      currentPrice={positionPrices[position.symbol]}
                      updating={null}
                      onViewDetails={() => {}}
                      onCloseFull={() => {}}
                      onCloseHalf={() => {}}
                      readOnly
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Market Thesis Preview */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Current Market Theses
          </h2>
          <MarketThesis usePublicApi={true} />
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg p-8 text-center text-white mb-8">
          <h2 className="text-2xl font-bold mb-2">
            Ready to Start Trading with Marlin Trades?
          </h2>
          <p className="text-primary-100 mb-6">
            Join the waitlist. automated trading
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="bg-white text-primary-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              Join the Waitlist
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/login"
              className="bg-primary-700 text-white border-2 border-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors"
            >
              Login to Your Account
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReadOnlyDashboard;
